import React from 'react';
import { JobParametersWithAddOn, JobType, Task, TaskStatus } from '../types';
import { DismissibleRightSidebar, Icon } from 'bench-internal-theme';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@material-ui/core';
import { useStyles } from './TaskSidebar.styles';
import { formatScreamingSnakesToPrettyStrings } from 'task-manager/formatScreamingSnakesToPrettyStrings';
import moment from 'moment';
import env from 'bench-environment';
import { StatusChip } from './StatusChip';
import { ResolveButton } from './ResolveButton';
import { TaskDetail, taskDetailsLookup } from './TaskDetails';
import { useTaskManagerContext } from 'task-manager/TaskManagerContext';
import { MicroFrontend } from 'micro-frontend';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { TaskSidebarAnalytics } from './taskSidebarAnalytics';
import { JobHistory } from './JobHistory';
import { ServiceInstructionLink } from './ServiceInstructionLink';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from '../../bk-web-app/features';

type TaskSidebarProps = {
  task: Task;
  dismissSidebar: () => void;
  preventDismiss?: (event: MouseEvent) => void;
  assignTask?: (task: Task) => Promise<Record<string, any>>;
  hideActionButtons?: boolean;
};

const dateFormat = 'MMM D, YYYY, hh:mm:ss a';

const TaskSidebar = ({
  task,
  dismissSidebar,
  preventDismiss,
  assignTask,
  hideActionButtons,
}: TaskSidebarProps) => {
  const { assignTaskLoading } = useTaskManagerContext();
  const { loggedInPerson } = useTaskManagerContext();
  const location = useLocation();

  const [featureState] = useFeatureFlags();
  const {
    featureContext: { features },
  } = featureState;
  const deprecateInstructionsMFEffOn =
    features[SplitFeatureFlags.DEPRECATE_SERVICE_INSTRUCTIONS_MFE]?.value ===
    FeatureValue.FEATURE_ON;

  const classes = useStyles();

  const getFormattedJobType = () => {
    return formatScreamingSnakesToPrettyStrings(task.job?.type);
  };

  const getFormattedTaskType = () => {
    return formatScreamingSnakesToPrettyStrings(task.type);
  };

  const getCreatedOn = () => {
    const createdOn = moment(new Date(task.createdOn)).format('MMMM D, YYYY'); // omit time
    const daysOld = moment().diff(new Date(createdOn), 'days'); // diff is relative to time 00:00:00
    return `${createdOn}, (${daysOld} ${daysOld === 1 ? 'day' : 'days'} old)`;
  };

  const isAddOnJobType = () => {
    return [
      JobType.RESOLVE_BULK_ADJUSTMENT,
      JobType.RESOLVE_3RD_PARTY_LOGIN_ADJUSTMENT,
      JobType.RESOLVE_DOCUMENT_ADJUSTMENT,
    ].includes(task.job.type);
  };

  const getAddOnId = () => {
    return (task.job.parameters as JobParametersWithAddOn)?.addOn?.id;
  };

  const showServiceInstructionsMFE = (): boolean => {
    return isAddOnJobType() && Boolean(getAddOnId());
  };

  const trackClick = (eventName: string) =>
    TaskSidebarAnalytics.sendHeapEvent(eventName, task, loggedInPerson.id);

  const handleOnClick = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    assignTask && assignTask(task);
    trackClick('Assign To Me Button Clicked');
  };

  return (
    <DismissibleRightSidebar dismissSidebar={dismissSidebar} preventDismiss={preventDismiss}>
      <Box className={classes.dialog}>
        <Box className={classes.headerWrapper}>
          <Typography variant="h2" className={classes.dialogTitle}>
            Task Id: {task.id}
          </Typography>
          <IconButton onClick={dismissSidebar} aria-label="close" className={classes.closeButton}>
            <Icon icon="close" />
          </IconButton>
        </Box>
        <Divider />
        <Accordion className={classes.topSection} defaultExpanded square elevation={0}>
          <AccordionSummary expandIcon={<Icon icon="arrowDown" viewBox="0 0 12 6" size="xsmall" />}>
            <Typography variant="h3" className={classes.sectionTitle}>
              Overview
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.topSectionInner}>
            <TaskDetail
              label="Status"
              content={
                <Box sx={{ display: 'flex' }}>
                  <StatusChip status={task.status} assignedPerson={task.assignedPerson} />
                  {task.resolution ? (
                    <Typography className={classes.resolutionText}>
                      {formatScreamingSnakesToPrettyStrings(task.resolution)}
                    </Typography>
                  ) : null}
                </Box>
              }
            />
            <TaskDetail
              label="Client"
              content={
                <Box className={classes.taskDetailContents}>
                  <Typography
                    className={classes.resolutionText}
                  >{`${formatScreamingSnakesToPrettyStrings(
                    task.job.client.inboxState,
                  )}, `}</Typography>
                  <RouterLink
                    to={{
                      pathname: location.pathname,
                      search: `${location.search}&clientIds=${task.job.client.id}`,
                    }}
                    className={classes.taskLink}
                  >
                    {task.job.client.id} (View Tasks)
                  </RouterLink>
                </Box>
              }
            />
            <TaskDetail label="Job" content={<Typography>{getFormattedJobType()}</Typography>} />
            <TaskDetail label="Task" content={<Typography>{getFormattedTaskType()}</Typography>} />
            <TaskDetail label="Created On" content={<Typography>{getCreatedOn()}</Typography>} />
            {task.job?.dueDate ? (
              <TaskDetail
                label="Due Date"
                content={
                  <Typography>
                    {moment.utc(new Date(task.job.dueDate)).format('MMMM D, YYYY')}
                  </Typography>
                }
              />
            ) : null}
          </AccordionDetails>
        </Accordion>
        <Box className={classes.detailsSection}>
          <Accordion className={classes.detailsSectionInner} defaultExpanded square elevation={0}>
            <AccordionSummary
              expandIcon={<Icon icon="arrowDown" viewBox="0 0 12 6" size="xsmall" />}
            >
              <Typography variant="h3" className={classes.sectionTitle}>
                Details
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.topSectionInner}>
              {taskDetailsLookup[task.job.type](task)}
              {showServiceInstructionsMFE() ? (
                deprecateInstructionsMFEffOn ? (
                  <ServiceInstructionLink addOnId={getAddOnId()} clientId={task.job.client.id} />
                ) : (
                  <MicroFrontend
                    name="GetBusinessService"
                    data={{ addOnId: getAddOnId(), clientId: task.job.client.id }}
                  />
                )
              ) : null}
            </AccordionDetails>
          </Accordion>
        </Box>
        <JobHistory jobId={task.job.id} taskId={task.id} />
        <Box className={classes.notesSectionHeader}>
          <Typography variant="h3" className={classes.sectionTitle}>
            Notes {task.job.taskNotes.length > 0 ? `(${task.job.taskNotes.length})` : ''}
          </Typography>
        </Box>
        <Box className={classes.notesSection}>
          <Box className={classes.notesInner}>
            <Box className={classes.notesLayout}>
              {task.job.taskNotes.map(note => (
                <Box key={`${note.id}`}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography className={classes.noteDetail}>
                      {note.createdBy.fullName}
                    </Typography>
                    <Typography className={classes.noteDetail}>
                      {moment(new Date(note.createdOn)).format(dateFormat)}
                    </Typography>
                  </Box>
                  <Box className={classes.noteText}>{note.body}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        {hideActionButtons ? null : (
          <Box className={classes.actionSection}>
            <Link
              href={`${env.location.bkTools}/${task.job.client.id}/`}
              className={classes.secondaryButton}
              underline="none"
              target="_blank"
              onClick={() => trackClick('View Client Button Clicked')}
            >
              View Client
            </Link>
            <AssignToMeButton
              task={task}
              handleOnClick={handleOnClick}
              assignTaskLoading={assignTaskLoading}
            />
            <ResolveButton task={task} />
          </Box>
        )}
      </Box>
    </DismissibleRightSidebar>
  );
};

const AssignToMeButton = ({
  task,
  handleOnClick,
  assignTaskLoading,
}: {
  task: Task;
  handleOnClick: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  assignTaskLoading: boolean;
}): React.ReactElement | null => {
  const classes = useStyles();
  const { loggedInPerson } = useTaskManagerContext();
  if (task.assignedPerson?.id && task.status === TaskStatus.PENDING_WORK) {
    const isAssignedToMe = task.assignedPerson.id === loggedInPerson.id;
    return (
      <Button
        variant="outlined"
        onClick={handleOnClick}
        className={classes.secondaryButton}
        disabled={assignTaskLoading}
      >
        Unassign {isAssignedToMe ? 'Me' : task.assignedPerson.fullName}
      </Button>
    );
  }

  if (task.status === TaskStatus.PENDING_WORK) {
    return (
      <Button
        onClick={handleOnClick}
        className={classes.primaryButton}
        disabled={assignTaskLoading}
      >
        Assign to Me
      </Button>
    );
  }
  return null;
};

export default TaskSidebar;
